import type { ActionKind, RoundEvent, Stance as CombatStance } from '@cs-shot/battle-sim';
import type { Scene } from './types';
import type { RoundUpdate } from '../game/useRushMidMatch';
import { ENTER_DUR, GREN_FLIGHT, KNIFE_LUNGE, ROUND_ANIM, playTime } from './battleConfig';
import { buildFighters, syncTo, type PlaybackState } from './playbackState';
import type { Actors } from './actors';

/** each fighter → their FIRST engaged target this round, across EVERY targeted action
 *  (shot / knife / grenade / flash / smoke), so the barrel + ring both track what the
 *  fighter actually did — not just gunfire. Without the non-shot kinds, a fighter who
 *  threw a nade/flash/knife had no entry, and the ring (falling back to the live pick)
 *  and the body (falling back to the nearest enemy) pointed at different enemies. */
export const buildRoundTargets = (events: RoundEvent[]): Map<string, string> => {
  const m = new Map<string, string>();
  const put = (shooter: string, target: string) => { if (!m.has(shooter)) m.set(shooter, target); };
  for (const e of events) {
    switch (e.type) {
      case 'shot': case 'knife': case 'grenade': case 'smoke': put(e.shooter, e.target); break;
      case 'flash': if (e.targets.length) put(e.shooter, e.targets[0]); break;
    }
  }
  return m;
};

/** shooters whose knife target is ALSO knifing them back (A→B and B→A) — they meet mid-way */
export const findMutualKnifers = (events: RoundEvent[]): Set<string> => {
  const knifeBy = new Map<string, string>();
  for (const e of events) if (e.type === 'knife') knifeBy.set(e.shooter, e.target);
  const out = new Set<string>();
  for (const [shooter, target] of knifeBy) if (knifeBy.get(target) === shooter) out.add(shooter);
  return out;
};

/** each fighter → the action they took this round, read off the round's events (so every
 *  name can show its stance colour + action icon during the replay, not just the local player). */
export const buildRoundActions = (events: RoundEvent[]): Map<string, ActionKind> => {
  const m = new Map<string, ActionKind>();
  for (const e of events) {
    switch (e.type) {
      case 'shot': if (!m.has(e.shooter)) m.set(e.shooter, 'shoot'); break;
      case 'grenade': m.set(e.shooter, 'grenade'); break;
      case 'smoke': m.set(e.shooter, 'smoke'); break;
      case 'flash': m.set(e.shooter, 'flash'); break;
      case 'knife': m.set(e.shooter, 'knife'); break;
      case 'heal': m.set(e.id, 'medkit'); break;
    }
  }
  return m;
};

/** A knife strike lands at the lunge APEX, not the event tick, so defer the deaths it causes
 *  to the apex too (else the victim falls before the knifer reaches it). Keyed by id@tick so a
 *  LATER (gun) death of the same fighter isn't moved. */
export const computeKnifeDeathTimes = (events: RoundEvent[], n: number): Map<string, number> => {
  const m = new Map<string, number>();
  for (const e of events) {
    if (e.type !== 'knife') continue;
    const apex = n + playTime(e.t) + KNIFE_LUNGE / 2;
    m.set(`${e.target}@${e.t}`, apex);
    m.set(`${e.shooter}@${e.t}`, apex);
  }
  return m;
};

// deterministic per-id stance (stable across replays of the same match), so the walk-in
// staggers the fighters "as if a round already passed" instead of one flat line.
const ENTRANCE_STANCES: CombatStance[] = ['attack', 'maneuver', 'defense'];
const entranceStance = (id: string): CombatStance => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ENTRANCE_STANCES[Math.abs(h) % ENTRANCE_STANCES.length];
};

/** Compile a round's events into the sorted playback timeline + pending-blast queue, seeding the
 *  scene from the previous round's end state. Mutates `state` in place. */
export const scheduleRound = (sc: Scene, state: PlaybackState, actors: Actors, u: RoundUpdate, n: number): void => {
  const firstBuild = !sc.fighters.length;
  // this round's authoritative fighter state (round-start): the round-0 build seeds the
  // scene's combat baseline from it, and it becomes the previous-round end for the next round.
  const snap = Object.fromEntries(u.fighters.map((f) => [f.id, { ...f }]));
  // round-0 cold start (a replay: no pre-round action-phase frame) → seed the scene + cursor from
  // the PRE-combat state so round 0 animates from full HP/armor. Without it `snap` is already
  // post-round-0, so round-0 damage double-applies / doesn't animate. Only set on round 0; every
  // other update leaves startSnap === snap (unchanged behaviour).
  const startSnap = u.initFighters
    ? Object.fromEntries(u.initFighters.map((f) => [f.id, { ...f }]))
    : snap;
  if (firstBuild) buildFighters(sc, state.inits, startSnap);

  // Cold start straight into a match already in progress (a spectator opening a live match, or a
  // player resuming mid-match): u.fighters is the CURRENT state, which already carries u.events
  // (the round that just resolved), and there is no previous round on screen to animate up from.
  // Replaying those events would apply their damage a SECOND time — bars flash to full, overshoot
  // downward, then snap back to the real value. Seed the scene to the live state in place and go
  // straight to live; the next round animates normally from here.
  if (firstBuild && u.round > 0) {
    syncTo(sc, snap);
    for (const f of sc.fighters) f.x = f.targetX; // skip the round-0 walk-in entrance
    state.cursor = Object.fromEntries(Object.entries(snap).map(([id, f]) => [id, { ...f }]));
    state.prevFinal = snap;
    state.timeline = [];
    state.pendingBlasts.length = 0;
    state.pendingSmokes.length = 0;
    state.pendingShots.length = 0;
    state.roundTargets = new Map();
    state.mutualKnifers = new Set();
    state.replayDur = 0;
    state.activeAt = n;
    return;
  }
  // ROUND 1 waits ENTER_DUR so the entrance (fighters walking in from their sides)
  // plays before any shots fire. Later rounds start immediately.
  if (firstBuild) state.enterAt = n;
  const base = firstBuild ? n + ENTER_DUR : n;
  // start this round from the previous round's end state (HP/armor/dead animate up)
  if (state.prevFinal) syncTo(sc, state.prevFinal);
  // The replay cursor starts where the scene starts — the previous round's end (round 0 /
  // a fresh mount: this round's start state). Each fired event advances it through battle-sim's
  // applyRoundEvent (see the engine's commit), so mid-replay hp/armor are the sim's own
  // numbers, never renderer arithmetic.
  state.cursor = Object.fromEntries(
    Object.entries(state.prevFinal ?? startSnap).map(([id, f]) => [id, { ...f }]),
  );
  state.prevFinal = snap;
  // …but STANCE + the chosen ACTION are THIS round's choice — show them from the replay start.
  // The server ships each fighter's RESOLVED action (chosen / auto-timeout / bot AI) in
  // `u.actions` — the true badge, so a fighter killed before its own tick still shows what it
  // did. `buildRoundActions(u.events)` is the fallback for old snapshots that lack the map.
  const eventActions = buildRoundActions(u.events);
  const serverActions = u.actions;
  const applyRoundStances = () => {
    for (const f of sc.fighters) {
      const s = snap[f.id];
      if (s) f.combatStance = s.stance;
      const server = serverActions?.[f.id];
      if (server) {
        f.roundAction = server.kind;
        // the server stance wins over the end-state combatStance for the shoot badge
        if (server.kind === 'shoot' && server.stance) f.combatStance = server.stance;
      } else if (serverActions) {
        // server map present but this fighter isn't in it → it didn't act (a corpse from an
        // earlier round) — stay bare, no default shoot.
        f.roundAction = eventActions.get(f.id) ?? null;
      } else {
        // old snapshot (no server map): derive from events; a fighter cut down before acting
        // emits none, so default it to shoot only if it was alive at round start. Corpses from
        // earlier rounds (dead on the replay cursor's start state) stay bare.
        const aliveAtStart = state.cursor?.[f.id]?.alive ?? false;
        f.roundAction = eventActions.get(f.id) ?? (aliveAtStart ? 'shoot' : null);
      }
      f.engagedId = null; // cleared each round; each shot re-sets the live target it fires on
    }
  };

  state.timeline = [];
  state.pendingBlasts.length = 0;
  state.pendingSmokes.length = 0;
  state.pendingShots.length = 0;
  if (firstBuild) {
    // stagger the walk-in with random stances, then snap to the real round-1 stances the
    // moment the entrance ends and the round's replay begins (badges appear then too).
    for (const f of sc.fighters) { f.combatStance = entranceStance(f.id); f.roundAction = null; }
    state.timeline.push({ at: base, run: applyRoundStances });
  } else {
    applyRoundStances();
  }
  const events = u.events;
  state.roundTargets = buildRoundTargets(events);
  state.mutualKnifers = findMutualKnifers(events);
  const knifeDeathAt = computeKnifeDeathTimes(events, base);

  // SMOKE persists into the NEXT round too (engine SMOKE_SHIELD_ROUNDS = 2). The standing cloud
  // is painted straight from `smokeScreens` every frame (drawSmokeScreen). An expired screen
  // isn't dropped — it gets a fade start so the cloud drifts away with this round's opening
  // instead of blinking off; drawSmokeScreen deletes it once fully faded.
  for (const s of state.smokeScreens.values()) {
    if (s.until < u.round && s.fadeAt == null) s.fadeAt = base;
  }

  // FLASH persists into the NEXT round too (engine FLASH_BLIND_ROUNDS = 2), exactly like the smoke
  // screen. The standing glare is painted straight from `blindGlows` every frame (drawBlindGlare)
  // and carries itself across rounds — no per-round re-pop. An expired glare isn't dropped; it gets
  // a fade start so the wash eases off with this round's opening instead of blinking, and
  // drawBlindGlare deletes it once fully faded.
  for (const g of state.blindGlows.values()) {
    if (g.until < u.round && g.fadeAt == null) g.fadeAt = base;
  }
  for (const ev of events) {
    if (ev.type !== 'smoke') continue;
    // anchor at the target's SETTLED battle spot, same reason as the grenade below: live x is
    // the map edge during the round-1 entrance, and a later knife lunge must not drag the cloud
    const tg = sc.byId[ev.target];
    // a RE-smoke on a still-covered fighter keeps the standing cloud alive (carry bornAt) and
    // just extends the coverage; a fresh/faded one waits invisible for its canister to land
    const prev = state.smokeScreens.get(ev.target);
    state.smokeScreens.set(ev.target, {
      until: u.round + 1, // covers this round + the next
      x: tg ? tg.spawnX : sc.W / 2,
      y: tg ? tg.y : sc.H / 2,
      ...(prev && prev.fadeAt == null && prev.bornAt != null ? { bornAt: prev.bornAt } : {}),
    });
  }

  for (const ev of events) {
    if (ev.type === 'shot') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyShot(ev) });
    } else if (ev.type === 'knife') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyKnife(ev) });
    } else if (ev.type === 'smoke') {
      const throwAt = base + playTime(ev.t);
      const land = throwAt + GREN_FLIGHT;
      state.timeline.push({ at: throwAt, run: () => actors.applySmokeThrow(ev, land) });
    } else if (ev.type === 'death') {
      const at = knifeDeathAt.get(`${ev.id}@${ev.t}`) ?? base + playTime(ev.t);
      state.timeline.push({ at, run: (nn) => actors.applyDeath(ev, nn) });
    } else if (ev.type === 'heal') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyHeal(ev) });
    } else if (ev.type === 'shield') {
      // adrenaline grant — arms the overshield right as the round's replay opens
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyShield(ev) });
    } else if (ev.type === 'flash') {
      const throwAt = base + playTime(ev.t);
      const land = throwAt + GREN_FLIGHT;
      state.timeline.push({ at: throwAt, run: () => actors.applyFlashThrow(ev, land) });
      state.timeline.push({ at: land, run: () => actors.applyFlashLand(ev, u.round) });
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closed union at compile time; guards RUNTIME events a NEWER server may send
    } else if (ev.type === 'grenade') {
      const land = base + playTime(ev.t);
      const throwAt = Math.max(base, land - GREN_FLIGHT);
      // capture the target's SETTLED battle spot (spawnX), not its live x — at round-1
      // schedule time the fighters are still off at the map edge (entrance), so live x
      // would fling the nade to the edge. spawnX is where the target sits when it lands.
      const tg = sc.byId[ev.target];
      const cx = tg ? tg.spawnX : sc.W / 2;
      const cy = tg ? tg.y - 80 : sc.H / 2;
      state.timeline.push({ at: throwAt, run: () => actors.applyGrenadeThrow(ev, land, cx, cy) });
      state.pendingBlasts.push({ at: land, cx, cy, ev });
    }
    // an event type this bundle doesn't know (newer server) is SKIPPED — an old client
    // loses the new animation, never crashes on a mis-parse (forward-compat rule, §4).
  }
  state.timeline.sort((a, b) => a.at - b.at);

  const maxT = events.length ? Math.min(ROUND_ANIM, Math.max(...events.map((e) => playTime(e.t)))) : 0;
  state.replayDur = events.length ? maxT + 0.4 : 0;
  state.activeAt = base;
};
